import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Process } from '../types';

export class ProcessService {
  private static readonly COLLECTION = 'processes';

  /**
   * Get all processes
   */
  static async getProcesses(): Promise<Process[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        orderBy('order', 'asc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Process[];
    } catch (error: any) {
      throw new Error(`Failed to fetch processes: ${error.message}`);
    }
  }

  /**
   * Get a specific process by ID
   */
  static async getProcess(processId: string): Promise<Process | null> {
    try {
      const docSnap = await getDoc(doc(db, this.COLLECTION, processId));
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
        } as Process;
      }
      return null;
    } catch (error: any) {
      throw new Error(`Failed to fetch process: ${error.message}`);
    }
  }

  /**
   * Create a new process
   */
  static async createProcess(process: Omit<Process, 'id'>): Promise<string> {
    try {
      const docRef = doc(collection(db, this.COLLECTION));
      const processData = {
        ...process,
        createdAt: Timestamp.now(),
      };
      await setDoc(docRef, processData);
      return docRef.id;
    } catch (error: any) {
      throw new Error(`Failed to create process: ${error.message}`);
    }
  }

  /**
   * Update an existing process
   */
  static async updateProcess(processId: string, updates: Partial<Process>): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION, processId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });
    } catch (error: any) {
      throw new Error(`Failed to update process: ${error.message}`);
    }
  }

  /**
   * Delete a process
   */
  static async deleteProcess(processId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, this.COLLECTION, processId));
    } catch (error: any) {
      throw new Error(`Failed to delete process: ${error.message}`);
    }
  }
}
